/**
 * Scorecard With Visibility Component
 * Main entry point for scorecard visibility feature
 * Handles mode switching between form view and table view
 */

import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { EmptyResults } from '@/components/ats';
import { CsvFailed } from '@/assets/icons/csv-export';
import {
  type GroupedScorecards,
  type ScorecardVisibilityMode,
} from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/types';
import { filterCurrentInterviewScorecards } from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/helpers';
import { ScorecardVisibilityView } from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/components/ScorecardVisibilityView';
import { type InterviewType } from '@/types/interview';

interface AllScorecardWithVisibilityProps {
  interview: InterviewType;
  visibilityMode?: ScorecardVisibilityMode;
  apiGroupedScorecards?: GroupedScorecards;
  hasCurrentUserSubmitted: boolean;
  tabId: string;
  refetchScorecardVisibility: () => void;
}

export const AllScorecardWithVisibility = ({
  interview,
  visibilityMode: propVisibilityMode,
  apiGroupedScorecards,
  hasCurrentUserSubmitted,
  tabId,
  refetchScorecardVisibility,
}: AllScorecardWithVisibilityProps) => {
  const currUser = useSelector(selectCurrUser);
  const visibilityMode = propVisibilityMode || 'never';

  const currentUserId = currUser?.id;

  const { scorecards: interviewScorecards, meeting_status } =
    (interview as InterviewType) || {};

  const isCandidateNoShowed = meeting_status === 'candidate_no_show';
  const hasNoScorecards = !interviewScorecards?.length;

  // const { canEditOthersScorecard } = useScorecardHelper({
  //   interview: interview as InterviewType,
  //   scorecards: [
  //     ...(apiGroupedScorecards?.currentInterview ?? []),
  //     ...(apiGroupedScorecards?.history ?? []),
  //   ] as ScorecardType[],
  // });

  // const currentUserScorecard = getCurrentUserScorecard(
  //   apiGroupedScorecards?.currentInterview ?? [],
  //   currentUserId
  // );

  // const hasCurrentUserScorecard = !!currentUserScorecard;

  // if the current user has a scorecard, we need to filter it out for other scorecards tab
  const currentInterviewScorecards = filterCurrentInterviewScorecards(
    apiGroupedScorecards?.currentInterview ?? [],
    currentUserId,
    tabId,
    interview.can_edit
  );

  const historyScorecards = apiGroupedScorecards?.history ?? [];

  const groupedScorecards = {
    currentInterview: currentInterviewScorecards,
    history: historyScorecards,
    transferInfo: apiGroupedScorecards?.transferInfo ?? null,
  };

  // Early returns for special cases
  if (isCandidateNoShowed) {
    return (
      <EmptyResults
        icon={CsvFailed}
        wrapperProps={{ h: '100%' }}
        text="Undo no-show to fill out the scorecard"
        iconProps={{ h: '140px', w: '142px', mb: '12px' }}
      />
    );
  }

  if (hasNoScorecards) {
    return (
      <EmptyResults
        icon={CsvFailed}
        wrapperProps={{ h: '100%' }}
        text="No scorecards to fill out for this interview"
        iconProps={{ h: '140px', w: '142px', mb: '12px' }}
      />
    );
  }

  return (
    <ScorecardVisibilityView
      groupedScorecards={groupedScorecards}
      currentUserId={currentUserId}
      mode={visibilityMode}
      hasCurrentUserSubmitted={hasCurrentUserSubmitted}
      interview={interview}
      tabId={tabId}
      refetch={refetchScorecardVisibility}
      // isLoading={isLoading}
    />
  );
};
